

import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/shared/Button';
import { ContactButtons } from '../components/shared/ContactButtons';
import { DownloadDropdown } from '../components/shared/DownloadDropdown';
import { FreeTrialButton } from '../components/shared/FreeTrialButton';
import { ServiceReviews } from '../components/shared/ServiceReviews'; // Importazione
import { ProcessedReviews } from '../components/shared/ProcessedReviews'; // NEW
import { InfiniteScrollReviews } from '../components/shared/InfiniteScrollReviews'; // NEW
import {
  ArrowLeft,
  TrendingUp,
  ExternalLink,
  FileText,
  ShieldCheck,
  Zap,
  Info,
  BarChart2,
  ShoppingCart,
  Gift,
  Download,
  Search
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES, EXTERNAL_LINKS, SERVICE_REVIEWS, DOCUMENTS } from '../constants'; // Importazione Dati

export const BlueStrikePage: React.FC = () => {
  const { t } = useLanguage();
  const [glossarySearch, setGlossarySearch] = useState('');

  return (
    <div className="bg-[#0B1120] text-white min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Navigation */}
        <div className="mb-12">
          <Link to={ROUTES.FINANCE} className="flex items-center text-gray-400 hover:text-white transition-colors group">
            <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            {t('common.back')}
          </Link>
        </div>

        {/* Hero Section */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end">
            <AnimatedSection>
              <div className="inline-block mb-6">
                <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
                  {t('finance.services.blueStrike.badge')}
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-6">
                CTC <span className="text-blue-500">BLUE STRIKE</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-10">
                {t('finance.services.blueStrike.detailedDesc')}
              </p>

              <div className="flex flex-wrap gap-4 mb-6">
                <a href={EXTERNAL_LINKS.BLUE_STRIKE_PRESENTATION} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-blue-600/20">
                    <FileText className="mr-2" size={20} />
                    {t('common.viewPresentation')}
                  </Button>
                </a>

                {/* PRICELIST DROPDOWN */}
                <DownloadDropdown
                  buttonText={t('common.downloadList')}
                  forceDirection="down"
                  options={[
                    { label: '🇮🇹 Italiano', url: DOCUMENTS.BLUE_STRIKE_PRICELIST_IT },
                    { label: '🇬🇧 English', url: DOCUMENTS.BLUE_STRIKE_PRICELIST_EN }
                  ]}
                />
              </div>


              {/* Amazon Banner */}
              <div className="bg-gradient-to-r from-[#FF9900]/10 to-transparent border border-[#FF9900]/30 rounded-xl p-6 flex items-start gap-4 hover:border-[#FF9900] transition-colors">
                <div className="bg-[#FF9900]/20 p-3 rounded-lg flex-shrink-0">
                  <ShoppingCart className="text-[#FF9900]" size={24} />
                </div>
                <div>
                  <h3 className="text-[#FF9900] font-bold text-lg mb-1 uppercase tracking-wide">
                    {t('finance.services.blueStrike.amazonBanner.title')}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {t('finance.services.blueStrike.amazonBanner.desc')}
                  </p>
                </div>
              </div>

            </AnimatedSection>

            {/* Tech Specs Box */}
            <AnimatedSection delay={200}>
              <div className="flex flex-col items-center">
                {/* Free Trial Button - Centered */}
                <div className="flex justify-center mb-12">
                  <FreeTrialButton
                    serviceName="CTC Blue Strike"
                    forceDirection="down"
                  />
                </div>

                {/* Technical Specs Box */}
                <div className="w-full bg-[#151E32] border border-gray-800 rounded-3xl p-6 sm:p-8 relative overflow-hidden z-10">
                  <div className="absolute top-0 right-0 p-6 opacity-5">
                    <TrendingUp size={200} />
                  </div>

                  <h3 className="text-xl font-bold mb-6 flex items-center">
                    <Info className="mr-2 text-blue-500" size={20} />
                    {t('common.technicalSpecs')}
                  </h3>
                  <div className="space-y-4 relative z-10">
                    {(t('finance.services.blueStrike.specs') as any[]).map((spec, i) => (
                      <div key={i} className="flex justify-between items-center border-b border-gray-800 pb-4 last:border-0 last:pb-0">
                        <span className="text-gray-500 text-sm font-medium">{spec.label}</span>
                        <span className="text-white font-bold">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Backtest Highlights Section */}
        <section className="mb-24">
          <AnimatedSection>
            <div className="bg-[#151E32] rounded-3xl border border-gray-800 p-10 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-cyan-400"></div>
              <h2 className="text-3xl font-bold mb-8 flex items-center">
                <BarChart2 className="mr-3 text-blue-500" />
                {t('finance.services.blueStrike.backtest.title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                <div className="p-4 bg-[#0B1120] rounded-xl border border-gray-800 text-center">
                  <div className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">{t('finance.services.blueStrike.backtest.netProfitLabel')}</div>
                  <div className="text-3xl font-black text-green-500">{t('finance.services.blueStrike.backtest.netProfit')}</div>
                  <div className="text-[10px] text-gray-500 mt-1">Compound Model</div>
                </div>
                <div className="p-4 bg-[#0B1120] rounded-xl border border-gray-800 text-center">
                  <div className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">{t('finance.services.blueStrike.backtest.maxDrawdownLabel')}</div>
                  <div className="text-3xl font-black text-blue-400">{t('finance.services.blueStrike.backtest.maxDrawdown')}</div>
                  <div className="text-[10px] text-gray-500 mt-1">Peak-to-Valley</div>
                </div>
                <div className="p-4 bg-[#0B1120] rounded-xl border border-gray-800 text-center">
                  <div className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">{t('finance.services.blueStrike.backtest.profitFactorLabel')}</div>
                  <div className="text-3xl font-black text-white">{t('finance.services.blueStrike.backtest.profitFactor')}</div>
                  <div className="text-[10px] text-gray-500 mt-1">Reliability</div>
                </div>
                <div className="p-4 bg-[#0B1120] rounded-xl border border-gray-800 text-center">
                  <div className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">{t('finance.services.blueStrike.backtest.totalTradesLabel')}</div>
                  <div className="text-3xl font-black text-orange-500">{t('finance.services.blueStrike.backtest.totalTrades')}</div>
                  <div className="text-[10px] text-gray-500 mt-1">Sample Size</div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </section>



        {/* MyFXBook API Dashboard Integration Info */}
        <section className="mb-24">
          <AnimatedSection>
            <div className="bg-gradient-to-r from-cyan-900/20 to-transparent border border-cyan-500/30 rounded-3xl p-10">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-cyan-500/20 p-3 rounded-lg flex-shrink-0">
                  <BarChart2 className="text-cyan-400" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-cyan-400 font-bold text-2xl mb-2 uppercase tracking-wide">
                    {t('finance.services.blueStrike.apiDashboard.title')}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    Le performance di CTC Blue Strike sono monitorate e certificate da MyFXbook. Trasparenza totale per permettere a ogni utente di verificare i rendimenti storici in tempo reale.
                  </p>

                  {/* Dashboard Link Button */}
                  <Link to={ROUTES.BLUE_STRIKE_DASHBOARD}>
                    <Button className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-3 px-6 rounded-lg shadow-lg shadow-cyan-600/20 mb-6">
                      <BarChart2 className="mr-2" size={20} />
                      Vai alla Dashboard Live
                    </Button>
                  </Link>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="bg-[#0B1120] p-4 rounded-lg border border-gray-800">
                      <h4 className="text-white font-bold text-sm mb-2">✓ {t('finance.services.blueStrike.apiDashboard.feature1')}</h4>
                      <p className="text-gray-500 text-xs">{t('finance.services.blueStrike.apiDashboard.feature1Desc')}</p>
                    </div>
                    <div className="bg-[#0B1120] p-4 rounded-lg border border-gray-800">
                      <h4 className="text-white font-bold text-sm mb-2">✓ {t('finance.services.blueStrike.apiDashboard.feature2')}</h4>
                      <p className="text-gray-500 text-xs">{t('finance.services.blueStrike.apiDashboard.feature2Desc')}</p>
                    </div>
                    <div className="bg-[#0B1120] p-4 rounded-lg border border-gray-800">
                      <h4 className="text-white font-bold text-sm mb-2">✓ {t('finance.services.blueStrike.apiDashboard.feature3')}</h4>
                      <p className="text-gray-500 text-xs">{t('finance.services.blueStrike.apiDashboard.feature3Desc')}</p>
                    </div>
                    <div className="bg-[#0B1120] p-4 rounded-lg border border-gray-800">
                      <h4 className="text-white font-bold text-sm mb-2">✓ {t('finance.services.blueStrike.apiDashboard.feature4')}</h4>
                      <p className="text-gray-500 text-xs">{t('finance.services.blueStrike.apiDashboard.feature4Desc')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </section>

        {/* RISULTATI E FEEDBACK */}
        <InfiniteScrollReviews
          service="Blue Strike"
          autoScroll={true}
        />

        {/* Glossary */}
        <section>
          <AnimatedSection>
            <h2 className="text-3xl font-bold mb-6 text-center uppercase tracking-wide">
              {t('common.glossary')}
            </h2>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={glossarySearch}
                  onChange={(e) => setGlossarySearch(e.target.value)}
                  placeholder={t('common.searchGlossary') || 'Cerca un termine...'}
                  className="w-full bg-[#151E32] border border-gray-800 rounded-xl pl-12 pr-4 py-4 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition-colors"
                />
                {glossarySearch && (
                  <button
                    onClick={() => setGlossarySearch('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(t('finance.services.blueStrike.glossary') as any[])
                .filter((item) => {
                  if (!glossarySearch) return true;
                  const searchLower = glossarySearch.toLowerCase();
                  return (
                    item.term.toLowerCase().includes(searchLower) ||
                    item.def.toLowerCase().includes(searchLower)
                  );
                })
                .map((item, i) => (
                  <div key={i} className="bg-[#151E32] border border-gray-800 rounded-2xl p-6 hover:border-blue-500/50 transition-colors">
                    <h4 className="text-blue-400 font-bold mb-3 text-lg border-b border-gray-700 pb-2">{item.term}</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">{item.def}</p>
                  </div>
                ))}
            </div>

            {/* No Results Message */}
            {glossarySearch && (t('finance.services.blueStrike.glossary') as any[]).filter((item) => {
              const searchLower = glossarySearch.toLowerCase();
              return (
                item.term.toLowerCase().includes(searchLower) ||
                item.def.toLowerCase().includes(searchLower)
              );
            }).length === 0 && (
                <div className="text-center py-12">
                  <Search className="mx-auto text-gray-600 mb-4" size={48} />
                  <p className="text-gray-400 text-lg">
                    {t('common.noResultsFound') || 'Nessun termine trovato'}
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    {t('common.tryAnotherKeyword') || 'Prova con un\'altra parola chiave'}
                  </p>
                </div>
              )}
          </AnimatedSection>
        </section>

      </div>
    </div>
  );
};